import type { LoginForm, RegisterForm } from '@/types/userTypes';
import type { LoginResponse } from '@/types/apiTypes';
import { http } from '@/utils/http';
import { unwrap } from '@/utils/apiClient';

// 配额详情
export interface QuotaDetail {
  type: string;       // daily_free/package/extra
  name: string;       // 日免费额度/专业版套餐
  used: number;       // 已用
  total: number;      // 总额
  remaining: number;  // 剩余
  percentage: number; // 使用百分比
  resetInfo: string;  // 今日重置/2025-01-10到期
  priority: number;   // 消耗优先级（1最高）
}

// 用户使用状态接口类型定义
export interface UserUsageInfo {
  // 核心字段 - 当前周期数据（统一使用）
  currentUsed: number;        // 当前周期已用
  currentLimit: number;       // 当前周期总额
  remainingCurrent: number;   // 当前周期剩余
  usagePercentage: number;    // 使用百分比

  // 周期信息
  periodType: string;         // 周期类型：daily/monthly/unlimited
  periodStart: string;        // 周期开始时间
  periodEnd: string;          // 周期结束时间

  // 展示信息
  quotaType: string;          // 配额类型：free/paid/mixed/guest
  displayText: string;        // 简洁提示文案（如："6,372 字符"）
  quotaDetails: QuotaDetail[]; // 配额明细列表
}

// 主套餐摘要信息
export interface PackageSummary {
  id: number;              // 套餐实例ID
  packageId: number;       // 套餐定义ID
  name: string;            // 套餐名称
  displayName: string;     // 显示名称（如：试用版 - 1天）
  versionCode: string;     // 版本代码（trial/basic/pro/enterprise）
  versionName: string;     // 版本名称（试用版/基础版/专业版）
  totalCredits: number;    // 总额度
  remainingCredits: number; // 剩余额度
  usedCredits: number;     // 已用额度
  expiresAt: string;       // 过期时间（ISO 8601格式）
  periodType: string;      // 周期类型：daily/monthly/fixed
}

export interface UserUsageStatusResponse {
  userTier: string;
  usageInfo: UserUsageInfo;
  packageSummary: PackageSummary | null;
  leadInfo: {
    shouldShowPrompt: boolean;
    promptType: string;
    promptTitle: string;
    promptMessage: string;
    ctaText: string;
    ctaLink: string;
    urgencyLevel: string;
  };
}

export const authApi = {
  /**
   * 用户登录
   * @param data 登录表单数据
   */
  login: (data: LoginForm) => {
    return unwrap<LoginResponse>(() => http.post('/api/v1/auth/mail/login', data));
  },

  /**
   * 用户注册
   * @param data 注册表单数据
   */
  register: (data: RegisterForm) => {
    return unwrap<null>(() => http.post('/api/v1/auth/mail/register', data));
  },

  /**
   * 用户登出
   */
  logout: () => {
    return unwrap<null>(() => http.post('/api/v1/auth/logout'));
  },

  /**
   * 发送邮箱验证码
   * @param email 邮箱地址
   */
  sendVerificationCode: (email: string) => {
    return unwrap<null>(() => http.post('/api/v1/auth/mail/verify-code', { email }));
  },

  /**
   * 获取用户信息
   */
  getUserInfo: () => {
    return unwrap<LoginResponse>(() => http.get('/api/v1/user/info'));
  },

  /**
   * 获取用户使用状态和引流信息
   */
  getUserUsageStatus: () => {
    return unwrap<UserUsageStatusResponse>(() => http.get('/api/v1/user/usage-status'));
  },

  /**
   * 获取用户使用统计图表数据
   */
  getUserUsageChart: (params?: { days?: number }) => {
    return unwrap<{ date: string; count: number }[]>(() => http.get('/api/v1/user/usage-chart', { params }));
  }
};